/* ==========================================================================
   EDUSTICK SHOPIFY THEME — base.js
   ========================================================================== */

(function () {
  'use strict';

  /* ============================================
     NAV — scrolled state
  ============================================ */
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      if (window.scrollY > 12) header.classList.add('scrolled');
      else header.classList.remove('scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ============================================
     SCROLL REVEAL
  ============================================ */
  const revealEls = document.querySelectorAll('.reveal, .reveal-stagger');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    revealEls.forEach((el) => io.observe(el));
  }

  /* ============================================
     MOBILE NAV
  ============================================ */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const mobileClose = document.querySelector('[data-mobile-close]');
  if (menuToggle && mobileNav) {
    const openMenu = () => {
      mobileNav.classList.add('open');
      document.body.classList.add('no-scroll');
    };
    const closeMenu = () => {
      mobileNav.classList.remove('open');
      document.body.classList.remove('no-scroll');
    };
    menuToggle.addEventListener('click', openMenu);
    if (mobileClose) mobileClose.addEventListener('click', closeMenu);
    // close on link tap
    mobileNav.querySelectorAll('a').forEach((a) => a.addEventListener('click', closeMenu));
    // close on ESC
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
  }

  /* ============================================
     CART DRAWER
  ============================================ */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartOverlay = document.querySelector('[data-cart-overlay]');
  const cartTriggers = document.querySelectorAll('[data-cart-open]');
  const cartCloseEls = document.querySelectorAll('[data-cart-close]');

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('open');
    if (cartOverlay) cartOverlay.classList.add('open');
    document.body.classList.add('no-scroll');
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('open');
    if (cartOverlay) cartOverlay.classList.remove('open');
    document.body.classList.remove('no-scroll');
  }
  cartTriggers.forEach((el) => {
    el.addEventListener('click', (e) => {
      // only intercept if cart drawer mode is on
      if (cartDrawer) {
        e.preventDefault();
        openCart();
      }
    });
  });
  // Use event delegation for close — cart drawer content is replaced after refreshCart,
  // so direct listeners on static elements don't survive innerHTML swaps.
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeCart();
    }
  });
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer && cartDrawer.classList.contains('open')) closeCart();
  });

  /* ============================================
     CART — fetch updates (Shopify AJAX API)
  ============================================ */
  async function refreshCart() {
    try {
      // Fetch the cart drawer section. Shopify Section Rendering API returns
      // the section wrapped in <div id="shopify-section-{id}">…</div>
      const res = await fetch('/?section_id=cart-drawer-content');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newInner = doc.querySelector('[data-cart-drawer-content-inner]');
      const currentInner = document.querySelector('[data-cart-drawer-content-inner]');
      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
      }

      // If on cart page, also refresh the cart page content
      const cartPageContent = document.querySelector('[data-cart-page-content]');
      if (cartPageContent) {
        const cartRes = await fetch('/cart?section_id=main-cart');
        const cartHtml = await cartRes.text();
        const cartDoc = new DOMParser().parseFromString(cartHtml, 'text/html');
        const newCartContent = cartDoc.querySelector('[data-cart-page-content]');
        if (newCartContent) {
          cartPageContent.innerHTML = newCartContent.innerHTML;
        }
      }

      // update count badge
      const cartRes2 = await fetch('/cart.js');
      const cartData = await cartRes2.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.setAttribute('data-count', cartData.item_count);
        el.textContent = cartData.item_count;
      });
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }
  window.refreshCart = refreshCart;

  /* Add-to-cart forms */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const originalText = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = 'Adding…';
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: new URLSearchParams(new FormData(form)).toString(),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCart();
      openCart();
      if (btn) {
        btn.innerHTML = '✓ Added';
        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }, 1500);
      }
    } catch (err) {
      console.error(err);
      if (btn) {
        btn.innerHTML = 'Error — try again';
        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }, 2000);
      }
    }
  });

  /* Cart quantity changes (drawer + cart page) */
  document.addEventListener('click', async (e) => {
    const inc = e.target.closest('[data-qty-inc]');
    const dec = e.target.closest('[data-qty-dec]');
    if (inc || dec) {
      // Prevent any parent form from receiving submit event when clicking inside it
      e.preventDefault();
      const wrap = (inc || dec).closest('.quantity');
      if (!wrap) return;
      const input = wrap.querySelector('input');
      if (!input) return;
      let v = parseInt(input.value, 10) || 1;

      // Stock cap — respect data-max on wrapper (set when inventory is tracked)
      const maxAttr = wrap.getAttribute('data-max') || input.getAttribute('max');
      const max = maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : null;

      if (inc) {
        if (max !== null && v >= max) {
          // Already at limit — visual nudge instead of incrementing
          input.classList.add('quantity-input--limit');
          setTimeout(() => input.classList.remove('quantity-input--limit'), 400);
          return;
        }
        v = v + 1;
      } else {
        v = Math.max(1, v - 1);
      }
      input.value = v;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.getAttribute('data-cart-remove');
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        await refreshCart();
      } catch (err) { console.error(err); }
    }
  });

  document.addEventListener('change', async (e) => {
    const qty = e.target.closest('[data-cart-qty]');
    if (!qty) return;
    const key = qty.getAttribute('data-cart-key');
    let value = parseInt(qty.value, 10) || 1;

    // Stock cap — clamp value to max if inventory tracked
    const maxAttr = qty.getAttribute('max');
    if (maxAttr !== null && maxAttr !== '') {
      const max = parseInt(maxAttr, 10);
      if (value > max) {
        value = max;
        qty.value = max;
        qty.classList.add('quantity-input--limit');
        setTimeout(() => qty.classList.remove('quantity-input--limit'), 400);
      }
    }
    if (value < 1) value = 1;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: value }),
      });
      await refreshCart();
    } catch (err) { console.error(err); }
  });

  /* ============================================
     PRODUCT GALLERY
  ============================================ */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-src');
        const alt = thumb.getAttribute('data-alt') || '';
        if (main && src) {
          const img = main.querySelector('img');
          if (img) { img.src = src; img.alt = alt; }
        }
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  });

  /* ============================================
     VARIANT PICKER (data-variant-picker controls product form variant)
  ============================================ */
  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    picker.querySelectorAll('button[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const id = btn.getAttribute('data-variant-id');
        const form = picker.closest('form') || document.querySelector(`#${picker.getAttribute('data-form-id')}`);
        if (!form) return;
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = id;
        // selected state
        picker.querySelectorAll('button[data-variant-id]').forEach((b) => b.setAttribute('aria-checked', 'false'));
        btn.setAttribute('aria-checked', 'true');
        // update displayed price/stock if present
        const price = btn.getAttribute('data-price');
        const priceEl = form.querySelector('[data-variant-price]');
        if (priceEl && price) priceEl.textContent = price;
        const stockEl = form.querySelector('[data-variant-stock]');
        const available = btn.getAttribute('data-available') === 'true';
        if (stockEl) {
          stockEl.style.display = available ? '' : 'none';
        }
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          if (available) {
            submitBtn.disabled = false;
            submitBtn.dataset.originalLabel = submitBtn.dataset.originalLabel || submitBtn.innerHTML;
            submitBtn.innerHTML = submitBtn.dataset.originalLabel;
          } else {
            submitBtn.disabled = true;
            submitBtn.dataset.originalLabel = submitBtn.dataset.originalLabel || submitBtn.innerHTML;
            submitBtn.innerHTML = 'Sold out';
          }
        }
        // update label "Selected: X"
        const labelEl = picker.querySelector('[data-selected-label]');
        if (labelEl) labelEl.textContent = btn.textContent.trim();
      });
    });
  });

  /* ============================================
     CONTACT FORM enhancement (inline thank you)
  ============================================ */
  document.querySelectorAll('[data-contact-form]').forEach((form) => {
    form.addEventListener('submit', () => {
      // Shopify handles the actual POST — we just show feedback on next page load
      const btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = 'Sending…';
      }
    });
  });
})();